import {
  fromDate,
  getLocalTimeZone,
  toCalendarDate,
  today,
} from "@internationalized/date"
import {
  type DateRange,
  prettyPrintDateRange,
} from "@prismicio/editor-support/Date"
import { composeRefs } from "@prismicio/editor-support/React"
import {
  type AriaDateRangePickerProps,
  type DateValue,
  useDateRangePicker,
} from "@react-aria/datepicker"
import {
  type DateRangePickerState,
  useDateRangePickerState,
} from "@react-stately/datepicker"
import { type ForwardedRef, forwardRef, useRef } from "react"

import { enLocale } from "../../utils/date"
import { Box } from "../Box"
import { Button } from "../Button"
import { RangeCalendar } from "../Calendar/Calendar"
import { Card, CardContent } from "../Card"
import { Popover } from "../Popover"
import { Tooltip } from "../Tooltip"

export interface DateRangePickerProps<Value extends DateRange | null> {
  value: Value
  minValue?: DateLimit
  maxValue?: DateLimit
  onSubmit?: (value: Value) => void
  disabled?: boolean
  triggerSize?: "small" | "medium" | "large"
  /** Accessible label for the date range picker */
  label?: string
  /** Fallback text to display if the date range picker has no value */
  fallback?: string
  /** Label for the reset button */
  resetLabel?: string
}

export const DateRangePicker = forwardRef(function DateRangePicker<
  Value extends DateRange | null,
>(props: DateRangePickerProps<Value>, ref: ForwardedRef<HTMLButtonElement>) {
  const {
    value,
    minValue,
    maxValue,
    onSubmit,
    disabled = false,
    triggerSize = "small",
    label,
    fallback = "No date range selected",
    resetLabel = "Reset",
    ...implicitProps
  } = props

  const triggerRef = useRef<HTMLButtonElement>(null)
  const initialValue = useRef(value)

  const timeZone = getLocalTimeZone()

  const datePickerProps: AriaDateRangePickerProps<DateValue> = {
    "aria-labelledby": "date-range-picker",
    placeholderValue: undefined,
    defaultValue: valueToAriaDateRange(value, timeZone),
  }

  const state = useDateRangePickerState({
    ...datePickerProps,
    shouldCloseOnSelect: false,
  })

  const { calendarProps } = useDateRangePicker(
    datePickerProps,
    state,
    triggerRef,
  )

  function toggle() {
    if (state.isOpen) {
      state.setOpen(false)
    } else {
      state.setValue(valueToAriaDateRange(value, timeZone))
      state.setOpen(true)
    }
  }

  function onPrimaryAction() {
    const { start, end } = state.value ?? {}

    if (start && end && onSubmit) {
      onSubmit({
        startDate: start.toDate(timeZone),
        endDate: end.toDate(timeZone),
      } as Value)
      toggle()
    }
  }

  function reset() {
    state.setOpen(false)

    state.setValue(valueToAriaDateRange(initialValue.current, timeZone))
    onSubmit?.(initialValue.current)
  }

  const isValid = !state.isInvalid && state.dateRange

  return (
    <Popover
      open={state.isOpen}
      align="start"
      sideOffset={4}
      anchor={
        <Button
          {...implicitProps}
          ref={composeRefs(triggerRef, ref)}
          size={triggerSize}
          color="grey"
          onClick={toggle}
          disabled={disabled}
          hiddenLabel={label}
          startIcon="calendarToday"
        >
          {getTriggerDisplayValue(value, fallback)}
        </Button>
      }
      onInteractOutside={toggle}
      onEscapeKeyDown={toggle}
    >
      <Card radius={4} paddingBlock={16}>
        <CardContent>
          <RangeCalendar
            {...calendarProps}
            minValue={getLimitDateValue(minValue, timeZone)}
            maxValue={getLimitDateValue(maxValue, timeZone)}
          />

          <Box
            gap={8}
            flexDirection="row"
            padding={{ top: 16 }}
            border={{ top: true }}
          >
            <Button color="grey" onClick={reset} sx={{ flexGrow: 1 }}>
              {resetLabel}
            </Button>
            <Tooltip content={getTooltipMessage(state)} visible={!isValid}>
              <Button
                onClick={onPrimaryAction}
                disabled={!isValid}
                sx={{ flexGrow: 1 }}
              >
                Apply
              </Button>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    </Popover>
  )
})

function valueToAriaDateRange(value: DateRange | null, timeZone: string) {
  if (!value) return null

  return {
    start: fromDate(value.startDate, timeZone),
    end: fromDate(value.endDate, timeZone),
  }
}

function getTriggerDisplayValue(value: DateRange | null, fallback: string) {
  if (!value) return fallback
  const locale = enLocale()

  return prettyPrintDateRange(value, { locale })
}

function getTooltipMessage(state: DateRangePickerState) {
  if (state.isInvalid) {
    return "Please select a start and end date"
  }

  if (!state.value?.end) {
    return "Please select an end date"
  }

  return ""
}

type DateLimit = Date | "today"

function getLimitDateValue(
  value: DateLimit | undefined,
  timeZone: string,
): DateValue | undefined {
  if (value === "today") {
    return today(timeZone)
  }
  if (value instanceof Date) {
    return toCalendarDate(fromDate(value, timeZone))
  }
  return undefined
}
